import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronDownIcon, CheckIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import apiClient from '../../lib/api-client';

interface Organization {
  id: string;
  name: string;
  slug?: string;
}

export const OrgSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, setToken } = useAuthStore();

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await apiClient.get('/auth/organizations');
      return res.data;
    },
    enabled: !!(user as any)?.is_admin,
  });

  const switchOrgMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiClient.post('/auth/switch-org', { organization_id: orgId });
      return res.data;
    },
    onSuccess: (data) => {
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      }
      setIsOpen(false);
      window.location.reload();
    },
  });

  // Only show for admin users
  if (!(user as any)?.is_admin || isLoading || organizations.length <= 1) {
    return null;
  }

  const currentOrg = organizations.find((org: Organization) => org.id === (user as any)?.organization_id);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        disabled={switchOrgMutation.isPending}
      >
        <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
        <span className="text-gray-700 max-w-32 truncate">
          {currentOrg?.name || 'Select Org'}
        </span>
        <ChevronDownIcon className="h-3 w-3 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50">
            <div className="py-1">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Organizations
              </div>
              {organizations.map((org: Organization) => (
                <button
                  key={org.id}
                  onClick={() => {
                    if (org.id !== (user as any)?.organization_id) {
                      switchOrgMutation.mutate(org.id);
                    } else {
                      setIsOpen(false);
                    }
                  }}
                  className="w-full text-left flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  disabled={switchOrgMutation.isPending}
                >
                  <span className="font-medium">{org.name}</span>
                  {org.id === (user as any)?.organization_id && (
                    <CheckIcon className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
            {switchOrgMutation.isPending && (
              <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500">
                Switching...
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrgSwitcher;
